import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, CardHeader, Field, Input } from '@truecare/ui';
import { unwrap, messageOf } from '@truecare/api-client';
import { api } from '../api';

/** Loose client-side email shape check — the backend Zod schema is the
 *  authority; this just keeps Save disabled on obviously-invalid input. */
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Shared account-profile card — the signed-in user's own display name + email.
 * Display name updates immediately (PATCH /auth/me). Email is VERIFY-FIRST: a
 * change requests a confirmation link to the new address (POST
 * /auth/email-change/request) and the address only changes once that link is
 * opened — so this card never mutates the email directly. Used by the facility
 * (and later homecare) account tab; distinct from the caregiver's professional
 * profile, which edits the Caregiver entity.
 */
export function AccountProfileCard() {
  const qc = useQueryClient();
  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => unwrap(api.GET('/api/v1/auth/me')),
  });
  const me = meQuery.data;

  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  // Set after a successful email-change request — drives the "check your inbox"
  // notice. Client-only (the pending change lives on the backend token).
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  const currentName = me?.displayName ?? '';
  const currentEmail = me?.email ?? '';
  const trimmedName = displayName.trim();
  const trimmedEmail = email.trim().toLowerCase();
  const nameChanged = trimmedName !== currentName;
  const emailChanged = trimmedEmail !== currentEmail.toLowerCase();
  const emailValid = EMAIL_RE.test(trimmedEmail);

  const save = useMutation({
    mutationFn: async () => {
      if (nameChanged) {
        await unwrap(
          api.PATCH('/api/v1/auth/me', { body: { displayName: trimmedName } }),
        );
      }
      if (emailChanged) {
        await unwrap(
          api.POST('/api/v1/auth/email-change/request', {
            body: { newEmail: trimmedEmail },
          }),
        );
      }
      return { emailChanged, newEmail: trimmedEmail };
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      setPendingEmail(r.emailChanged ? r.newEmail : null);
      setEditing(false);
    },
  });

  function startEdit(): void {
    setDisplayName(currentName);
    setEmail(currentEmail);
    save.reset();
    setEditing(true);
  }

  const canSave =
    (nameChanged || emailChanged) &&
    trimmedName.length > 0 &&
    (!emailChanged || emailValid);

  return (
    <Card padding="lg" accent="left" tone="forest">
      <CardHeader
        title="Your account"
        description="Your personal sign-in — your name and login, just for you."
        action={
          !editing ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={startEdit}
              data-testid="account-profile-edit"
            >
              Edit
            </Button>
          ) : undefined
        }
      />

      {!editing && pendingEmail && (
        <Alert tone="info" title="Confirm your new email" className="mt-4">
          We&rsquo;ve sent a confirmation link to <strong>{pendingEmail}</strong>.
          Your sign-in email changes only once you open that link — until then,
          keep using <strong>{currentEmail}</strong>.
        </Alert>
      )}

      {editing ? (
        <div className="mt-4 flex flex-col gap-4">
          <Field label="Display name" hint="The name shown across TrueCare™.">
            <Input
              value={displayName}
              maxLength={120}
              onChange={(e) => setDisplayName(e.target.value)}
              data-testid="account-display-name"
            />
          </Field>
          <Field
            label="Email"
            hint="Changing this sends a confirmation link to the new address — your email changes only after you open it."
          >
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="account-email"
            />
          </Field>
          {emailChanged && trimmedEmail.length > 0 && !emailValid && (
            <p className="font-body text-xs text-danger">
              Enter a valid email address.
            </p>
          )}
          {save.isError && (
            <Alert tone="danger">{messageOf(save.error)}</Alert>
          )}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={!canSave}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-2">
          <div>
            <dt className="font-body text-xs font-semibold uppercase tracking-wide text-ink-muted">
              Display name
            </dt>
            <dd className="mt-0.5 font-body text-sm text-ink">
              {me?.displayName ?? '—'}
            </dd>
          </div>
          <div>
            <dt className="font-body text-xs font-semibold uppercase tracking-wide text-ink-muted">
              Email
            </dt>
            <dd className="mt-0.5 font-body text-sm text-ink">{me?.email ?? '—'}</dd>
          </div>
        </dl>
      )}
    </Card>
  );
}
